/**
 * Centralized Plan/Frequency Configuration
 * 
 * This is the SINGLE SOURCE OF TRUTH for mapping plans to frequencies.
 * All components and edge functions should import from this file.
 */

export type PlanKey = 
  | 'presence' 
  | 'presence-multihook' 
  | 'emergence' 
  | 'emergence-multihook' 
  | 'acceleration' 
  | 'acceleration-multihook';

export interface PlanConfig {
  frequency: number;        // Videos per week (1, 3, or 5)
  label: string;            // Display label
  labelShort: string;       // Short display label for badges
  postsPerWeek: number;     // Same as frequency (for clarity)
  isAlternating?: boolean;  // True for emergence (alternates 3/2)
  weekADays?: number;       // Days in week A (for alternating plans)
  weekBDays?: number;       // Days in week B (for alternating plans)
  color: string;            // Badge color classes
}

/**
 * Master configuration for all plans
 */
export const PLAN_CONFIG: Record<PlanKey, PlanConfig> = {
  presence: {
    frequency: 1,
    label: 'Présence - 1 vidéo/semaine',
    labelShort: 'Présence',
    postsPerWeek: 1,
    color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  },
  'presence-multihook': {
    frequency: 1,
    label: 'Présence Multihook - 1 vidéo/semaine',
    labelShort: 'Présence MH',
    postsPerWeek: 1,
    color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  },
  emergence: {
    frequency: 3,
    label: 'Émergence - 10 vidéos/mois',
    labelShort: 'Émergence',
    postsPerWeek: 3,
    isAlternating: true,
    weekADays: 3,
    weekBDays: 2,
    color: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  },
  'emergence-multihook': {
    frequency: 3,
    label: 'Émergence Multihook - 10 vidéos/mois',
    labelShort: 'Émergence MH',
    postsPerWeek: 3,
    isAlternating: true,
    weekADays: 3,
    weekBDays: 2,
    color: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  },
  acceleration: {
    frequency: 5,
    label: 'Accélération - 5 vidéos/semaine',
    labelShort: 'Accélération',
    postsPerWeek: 5,
    color: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  },
  'acceleration-multihook': {
    frequency: 5,
    label: 'Accélération Multihook - 5 vidéos/semaine',
    labelShort: 'Accélération MH',
    postsPerWeek: 5,
    color: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  },
};

/**
 * All available plan options for select dropdowns
 */
export const PLAN_OPTIONS: Array<{ value: PlanKey; label: string }> = [
  { value: 'presence', label: 'Présence' },
  { value: 'presence-multihook', label: 'Présence Multihook' },
  { value: 'emergence', label: 'Émergence' },
  { value: 'emergence-multihook', label: 'Émergence Multihook' },
  { value: 'acceleration', label: 'Accélération' },
  { value: 'acceleration-multihook', label: 'Accélération Multihook' },
];

/**
 * Convert a plan string to its frequency (videos per week)
 * @param plan - Plan identifier (e.g., 'acceleration', 'emergence-multihook')
 * @returns Frequency number (1, 3, or 5). Defaults to 3 if plan not found.
 */
export function planToFrequency(plan: string | null | undefined): number {
  if (!plan) return 3; // Default to emergence
  const normalizedPlan = plan.toLowerCase().trim() as PlanKey;
  return PLAN_CONFIG[normalizedPlan]?.frequency ?? 3;
}

/**
 * Convert a frequency to the base plan name
 * @param frequency - Videos per week (1, 3, or 5)
 * @returns Base plan name (without multihook suffix)
 */
export function frequencyToPlan(frequency: number | null | undefined): PlanKey {
  if (frequency === 5 || frequency === 4) return 'acceleration';
  if (frequency === 1) return 'presence';
  return 'emergence'; // Default for 2, 3, or undefined
}

/**
 * Get the configuration for a plan
 * @param plan - Plan identifier
 * @returns PlanConfig or null if not found
 */
export function getPlanConfig(plan: string | null | undefined): PlanConfig | null {
  if (!plan) return null;
  const normalizedPlan = plan.toLowerCase().trim() as PlanKey;
  return PLAN_CONFIG[normalizedPlan] ?? null;
}

/**
 * Get display label for a plan
 * @param plan - Plan identifier
 * @param short - Whether to return short label (default: false)
 * @returns Display label or null if plan not found
 */
export function getPlanLabel(plan: string | null | undefined, short: boolean = false): string | null {
  const config = getPlanConfig(plan);
  if (!config) return null;
  return short ? config.labelShort : config.label;
}

/**
 * Get color classes for a plan badge
 * @param plan - Plan identifier
 * @returns Tailwind color classes string
 */
export function getPlanColor(plan: string | null | undefined): string {
  const config = getPlanConfig(plan);
  return config?.color ?? 'bg-white/10 text-white/60 border-white/20';
}

/**
 * Check if a plan is of "emergence" type (has alternating weeks)
 * @param plan - Plan identifier
 */
export function isEmergencePlan(plan: string | null | undefined): boolean {
  if (!plan) return false;
  return plan.toLowerCase().startsWith('emergence');
}

/**
 * Check if a plan is of "presence" type
 * @param plan - Plan identifier
 */
export function isPresencePlan(plan: string | null | undefined): boolean {
  if (!plan) return false;
  return plan.toLowerCase().startsWith('presence');
}

/**
 * Check if a plan is of "acceleration" type
 * @param plan - Plan identifier
 */
export function isAccelerationPlan(plan: string | null | undefined): boolean {
  if (!plan) return false;
  return plan.toLowerCase().startsWith('acceleration');
}

/**
 * Check if a plan has the multihook variant
 * @param plan - Plan identifier
 */
export function isMultihookPlan(plan: string | null | undefined): boolean {
  if (!plan) return false;
  return plan.toLowerCase().includes('multihook');
}
